import { useTranslation } from 'react-i18next';
import { simNudge, simOffset } from '../sim/simControl';
import { useStore } from '../store';
import { Lightbar, SteerArrow } from './deviation';

export function DrivingHud() {
  const { t } = useTranslation();
  const guidance = useStore((s) => s.guidance);
  const guidanceUi = useStore((s) => s.guidanceUi);
  const setGuidanceUi = useStore((s) => s.setGuidanceUi);
  const stopDriving = useStore((s) => s.stopDriving);
  const pauseDriving = useStore((s) => s.pauseDriving);
  const resumeDriving = useStore((s) => s.resumeDriving);
  const fix = useStore((s) => s.fix);
  const phase = useStore((s) => s.phase);
  const coveragePct = useStore((s) => s.coveragePct);
  const coverageM2 = useStore((s) => s.coverageM2);
  const gnssSource = useStore((s) => s.serverConfig?.gnssSource ?? 'sim');
  const simEnabled = useStore((s) => s.simEnabled);
  const showSimControls = useStore((s) => s.showSimControls);
  const paused = phase === 'paused';
  // Simulator nudge/recenter — dev-testing aid; never on the real (phone/serial) HUD, and not while
  // paused (nothing's moving). Active for either the server sim or the browser test sim.
  const simControls = (gnssSource === 'sim' || simEnabled) && showSimControls && !paused;

  return (
    <>
      {/* Deviation widget + display toggle — the primary driving readout, so make it prominent. */}
      <div className="pointer-events-auto absolute inset-x-0 top-14 mx-auto flex w-full max-w-2xl flex-col items-center gap-3 px-3 md:top-16 md:gap-4 md:px-4 lg:max-w-3xl">
        <div className="w-full rounded-3xl bg-black/60 px-5 py-6 shadow-2xl backdrop-blur md:px-8 md:py-7">
          {guidance ? (
            guidanceUi === 'arrow' ? (
              <SteerArrow g={guidance} />
            ) : (
              <Lightbar g={guidance} />
            )
          ) : (
            <div className="text-center text-white/60">{t('gps.searching')}</div>
          )}
          {guidance && (
            <div className="mt-3 text-center text-sm text-white/50">
              {t('driving.lane', { n: guidance.activeLaneIndex })}
            </div>
          )}
        </div>

        <div className="flex gap-1 rounded-full bg-black/55 p-1 text-sm backdrop-blur">
          <Toggle on={guidanceUi === 'arrow'} onClick={() => setGuidanceUi('arrow')}>
            {t('driving.modeArrow')}
          </Toggle>
          <Toggle on={guidanceUi === 'lightbar'} onClick={() => setGuidanceUi('lightbar')}>
            {t('driving.modeLightbar')}
          </Toggle>
        </div>
      </div>

      {/* Left cluster: metadata (speed + coverage) + the small drive controls, grouped together. */}
      <div className="bottom-safe pointer-events-auto absolute left-3 flex flex-col gap-2 rounded-2xl bg-black/55 px-3 py-3 backdrop-blur md:left-4 md:min-w-[13rem] md:gap-3 md:px-4 md:py-4">
        {paused && (
          <div className="rounded-xl bg-amber-500/90 px-3 py-1 text-center text-sm font-bold uppercase tracking-wide text-black">
            {t('driving.paused')}
          </div>
        )}

        <div>
          <div className="text-xl font-semibold tabular-nums text-white md:text-2xl">
            {t('gps.speed', { kmh: ((fix?.speed ?? 0) * 3.6).toFixed(1) })}
          </div>
          <div className="mt-0.5 text-[11px] uppercase tracking-wide text-white/50">
            {t('driving.covered')}
          </div>
          <div className="text-sm font-medium tabular-nums text-sky-300 md:text-base">
            {t('driving.coveredValue', {
              pct: Math.round(coveragePct),
              ha: (coverageM2 / 10000).toFixed(2),
            })}
          </div>
        </div>

        {/* Pause while driving; Continue + Stop once paused. */}
        <div className="flex gap-2">
          {paused ? (
            <>
              <button
                onClick={resumeDriving}
                className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white active:scale-[0.99]"
              >
                ▶ {t('driving.continue')}
              </button>
              <button
                onClick={stopDriving}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white active:scale-[0.99]"
              >
                {t('driving.stop')}
              </button>
            </>
          ) : (
            <button
              onClick={pauseDriving}
              className="flex-1 rounded-xl bg-amber-500 py-2.5 text-sm font-bold text-black active:scale-[0.99]"
            >
              {t('driving.pause')}
            </button>
          )}
        </div>

        {/* Simulator nudge controls — clearly marked, dev-testing only. */}
        {simControls && (
          <div className="flex items-center gap-2">
            <span className="rounded bg-fuchsia-600/90 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              {t('driving.simDev')}
            </span>
            <button
              onClick={() => simNudge(-0.2)}
              aria-label={t('driving.nudgeLeft')}
              className="flex-1 rounded-lg border border-dashed border-fuchsia-400/60 bg-black/40 py-1.5 text-sm font-semibold text-white active:scale-[0.99]"
            >
              ←
            </button>
            <button
              onClick={() => simOffset(0)}
              aria-label={t('driving.recenter')}
              className="rounded-lg border border-dashed border-fuchsia-400/60 bg-black/40 px-2.5 py-1.5 text-sm text-white"
            >
              ◎
            </button>
            <button
              onClick={() => simNudge(0.2)}
              aria-label={t('driving.nudgeRight')}
              className="flex-1 rounded-lg border border-dashed border-fuchsia-400/60 bg-black/40 py-1.5 text-sm font-semibold text-white active:scale-[0.99]"
            >
              →
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Toggle({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 font-medium ${on ? 'bg-white text-zinc-900' : 'text-white/70'}`}
    >
      {children}
    </button>
  );
}
